"use client";

import { useEffect, useId, useState } from "react";
import { cn } from "@suite/lib/utils";

interface NumberFieldProps {
  label: string;
  hint?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min: number;
  max: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  /** When true an empty field means "not set" (null) rather than snapping back to `min`. */
  optional?: boolean;
  disabled?: boolean;
}

const fieldClass =
  "border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full min-w-0 rounded-lg border px-3 text-sm tabular-nums outline-none focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Number input that keeps its own text while the user types (so clearing the field or typing
 * "0." doesn't fight the parent's clamped number) and commits on blur / valid keystrokes.
 */
export function NumberField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix,
  suffix,
  optional = false,
  disabled = false,
}: NumberFieldProps) {
  const id = useId();
  const [text, setText] = useState(value === null ? "" : String(value));

  useEffect(() => {
    // External changes (benchmark preset, share link) overwrite what's typed.
    setText(value === null ? "" : String(value));
  }, [value]);

  const commit = (raw: string) => {
    if (raw.trim() === "") {
      onChange(optional ? null : min);
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) onChange(Math.min(max, Math.max(min, parsed)));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-foreground text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        {prefix && (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={text}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            commit(e.target.value);
          }}
          onBlur={() => setText(value === null ? "" : String(value))}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className={cn(fieldClass, prefix && "pl-7", suffix && "pr-12")}
        />
        {suffix && (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs">
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <p id={`${id}-hint`} className="text-muted-foreground text-xs leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
}

interface SliderFieldProps {
  label: string;
  hint?: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Rendered next to the label, e.g. "85%". */
  display: string;
}

export function SliderField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 1,
  display,
}: SliderFieldProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-foreground text-sm font-medium">
          {label}
        </label>
        <output htmlFor={id} className="text-foreground text-sm font-semibold tabular-nums">
          {display}
        </output>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="accent-brand-green h-2 w-full cursor-pointer"
      />
      {hint && (
        <p id={`${id}-hint`} className="text-muted-foreground text-xs leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
  detail?: string;
  emphasis?: boolean;
}

export function ResultStat({ label, value, detail, emphasis = false }: StatProps) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd
        className={cn(
          "text-foreground font-semibold tabular-nums",
          emphasis ? "text-4xl sm:text-5xl" : "text-2xl"
        )}
      >
        {value}
      </dd>
      {detail && <p className="text-muted-foreground text-xs leading-relaxed">{detail}</p>}
    </div>
  );
}

interface SegmentedToggleProps<T extends string> {
  label: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (value: T) => void;
}

export function SegmentedToggle<T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedToggleProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className="bg-foreground/5 grid auto-cols-fr grid-flow-col gap-1 rounded-lg p-1"
    >
      {options.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.id)}
            className={cn(
              "rounded-md px-3 py-2 text-sm font-medium transition-colors",
              active
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
